// Funding-source catalog — the value is a fixed contract with the API /
// squad-rewards ledger (see loyalty.controller.js's VALID_FUNDING_SOURCES); the
// label is display-only and owned here. Which of these a reviewer is actually
// offered is narrowed at runtime by LoyaltyConfig.enabledFundingSources
// (managed on the Loyalty Program Rules screen).
export const FUNDING_SOURCES = [
    { value: 'our_pl', label: 'Our P&L (standard 5% back, subject to the annual cap)' },
    { value: 'client_promo', label: 'Client promo (CPG-funded, uncapped)' },
    { value: 'b5g50', label: 'Buy 5 Get 50' },
];

// Given the API's enabledFundingSources (or null/undefined when the config
// hasn't loaded), return the FUNDING_SOURCES to actually show. Unknown = show
// all, so a config fetch failure degrades to the previous behaviour rather than
// an empty dropdown.
export const availableFundingSources = (enabled) => (
    Array.isArray(enabled)
        ? FUNDING_SOURCES.filter((f) => enabled.includes(f.value))
        : FUNDING_SOURCES
);

// ---- Points history (GET /admin/loyalty/members/:id/balance → squad-rewards) ----
// squad-rewards' balance response carries the member's full all-time history
// as a mix of two row shapes from two tables (loyalty_earnings /
// loyalty_redemptions). A redemption row is `type: 'redemption'` and carries
// `kind` + `cashCents` + a negative-signed display amount; an earn row carries
// `sourceType`. Labels mirror the member-facing Loyalty Center
// (somo-circle1-membership-frontend's LoyaltyBalance.js) so admin and member
// see identical wording.
const REDEMPTION_KIND_LABELS = { gift_card: 'a gift card', fee_credit: 'a membership credit' };
const SOURCE_TYPE_LABELS = { receipt: 'Receipt scan' };

export const formatPoints = (points) => (
    Number(points).toLocaleString(undefined, { maximumFractionDigits: 1 })
);

export const formatHistoryCents = (cents) => (
    `$${((Number.isFinite(cents) ? cents : 0) / 100).toFixed(2)}`
);

// Drop a row missing the fields its render branch needs, rather than showing
// "$NaN" / "Invalid Date" / a React key collision — same guard as the member
// view's normalizeBalance. `id` can collide across the two source tables, so
// callers key on `${type}-${id}`.
export const isValidHistoryItem = (h) => {
    if (!h || !Number.isFinite(h.points) || typeof h.date !== 'string' || h.id == null) return false;
    if (h.type === 'redemption') return Number.isFinite(h.cashCents) && typeof h.kind === 'string';
    return true;
};

export const historyItemLabel = (h) => (h.type === 'redemption'
    ? `Redeemed as ${REDEMPTION_KIND_LABELS[h.kind] || h.kind}`
    : (SOURCE_TYPE_LABELS[h.sourceType] || 'Points earned'));

export const historyItemPoints = (h) => (h.type === 'redemption'
    ? `-${formatPoints(h.points)} pts (${formatHistoryCents(h.cashCents)})`
    : `+${formatPoints(h.points)} pts`);

// somo-circle1-api#796: an earn-rate fraction as a percent, e.g. 0.05 -> "5%",
// 0.025 -> "2.5%". Shared by the review screen's picker and the queue label.
export const formatEarnPercent = (fraction) => `${Math.round(fraction * 10000) / 100}%`;

// Where an approved receipt was marked as bought and the rate it got, e.g.
// "Off base · 1%". Receipts approved before #796 have no earnLocation and get
// null (nothing shown); an unexpected value is shown as-is, not as on base.
const EARN_LOCATION_LABELS = { on_base: 'On base', off_base: 'Off base' };
export const earnLocationLabel = (earnLocation, earnPercentageApplied) => {
    if (!earnLocation) return null;
    const place = EARN_LOCATION_LABELS[earnLocation] || earnLocation;
    return Number.isFinite(earnPercentageApplied)
        ? `${place} · ${formatEarnPercent(earnPercentageApplied)}`
        : place;
};

// #209: OCR's not_commissary flag (no line says "COMMISSARY"). Since #796
// that's usually an Exchange (on base) or off-base receipt, both of which
// earn, so the label asks the reviewer which rather than calling it invalid.
// Shared by the queue, the Edit / Review screen and the Unclaimed screen.
export const NOT_COMMISSARY_LABEL = 'Not a commissary: Exchange or off base?';

// ---- Unlock with points (somo-circle1-api#791/#792, admin #199 Phase 2) ----
// Shared by the Membership Unlocks screen and the user-detail Membership rows.
export const UNLOCK_STATUS_LABEL = {
    pending: 'Pending',
    processing: 'Processing',
    awaiting_payment: 'Awaiting card payment',
    completed: 'Completed',
    failed: 'Failed (member can retry)',
    needs_support: 'Needs support',
};

export const UNLOCK_MODE_LABEL = {
    points: 'Points only',
    points_and_card: 'Points + card',
};

// Why a stuck row is stuck, in support's words. Covers every branch of the
// API's MembershipUnlockRequest.stuckFilter.
export function unlockStuckReason(u) {
    if (u.status === 'needs_support') return 'Points spent; retrying can\'t finish it.';
    if (!u.pointsSpentAt && u.spendUnknownAt) return 'Points spend never confirmed; squad-rewards may hold them.';
    if (u.status === 'awaiting_payment') return 'Points credited in Stripe; the member never paid the card part.';
    return 'Points spent; the unlock stopped part-way.';
}
